import { useCallback, useEffect, useState } from 'react';
import {
    initFileGlobal,
    cleanFileGlobal,
    storeLoadedFileGlobal,
    setGlobalFunction,
} from './globals-modifier';

import { handleSelectedFiles, saveFile } from './file-utils';

const LIBRARY_HANDLER_OBJECT_NAME = '[FGFileBrowser]';

/**
 *
 * custom React hook to handle `fg-file-browser` interaction
 *
 * @param {function} sendMessage
 * @return {UseFGFileBrowserResult}
 *
 */
function useFGFileBrowser(sendMessage) {
    const [filePopupVisible, setFilePopupVisible] = useState(false);
    const [acceptTypes, setAcceptTypes] = useState('');

    /**
     *
     * Wrapped version of `sendMessage` that always sends to '[FGFileBrowser]'
     *
     * @param {string} methodName
     * @param {any=} parameter
     *
     */
    const sendMessageFGFile = useCallback(
        (methodName, parameter) => {
            sendMessage(LIBRARY_HANDLER_OBJECT_NAME, methodName, parameter);
        },
        [sendMessage]
    );

    useEffect(() => {
        function callFBFunctionByName(functionName, parameter) {
            console.log('callFBFunctionByName', { functionName, parameter });
            switch (functionName) {
                case 'openFileBrowserForLoad':
                    setFilePopupVisible(true);
                    setAcceptTypes(parameter[0]);
                    return;
                case 'cleanupFB':
                    cleanFileGlobal();
                    setFilePopupVisible(false);
                    return;
                case 'closeFileBrowserForOpen':
                    setFilePopupVisible(false);
                    return;
                case 'saveFile':
                    saveFile(parameter, (fileInfo) => {
                        sendMessageFGFile(
                            'HandleFileSaved',
                            JSON.stringify(fileInfo)
                        );
                    });
                    return;
                case 'initializeFBLibrary':
                case 'setLocalization':
                default:
                    return;
            }
        }

        initFileGlobal();
        setGlobalFunction(callFBFunctionByName);
    }, [sendMessageFGFile]);

    function handleHide() {
        setFilePopupVisible(false);
        sendMessageFGFile('CloseFileBrowserForOpen');

        // reset to empty
        setAcceptTypes('');
    }

    function handleFilesSelection(fileList) {
        const files = Array.from(fileList);

        sendMessageFGFile('SetAmountOfFilesToBeLoaded', files.length);

        handleSelectedFiles(files, (loadedFile, loadedFileBuffer) => {
            storeLoadedFileGlobal(loadedFile.fullName, loadedFileBuffer);

            sendMessageFGFile('HandleLoadedFile', JSON.stringify(loadedFile));
        });
    }

    /**
     *
     * @typedef {Object} UseFGFileBrowserResult
     * @property {function} handleHide
     * @property {function} handleFilesSelection
     * @property {boolean} filePopupVisible - whether to display the UI element which will render an `<input type="file" />`
     * @property {string} acceptTypes - should be used for the accept prop on rendered input e.g. `<input type="file" accept=".pdf">`
     *
     */
    return {
        handleHide,
        handleFilesSelection,
        filePopupVisible,
        acceptTypes,
    };
}

export default useFGFileBrowser;
